import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import styles from './ManageEnterprises.module.scss'
import sharedStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { isApiError } from '@/api'
import { getPendingInvoice, sendEnterpriseInvoice } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'

interface PendingInvoiceSectionProps {
  enterpriseId: string
}

const PendingInvoiceSection = ({ enterpriseId }: PendingInvoiceSectionProps) => {
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false)
  const [poNumber, setPoNumber] = useState('')
  const { updateToast } = useToast()
  const queryClient = useQueryClient()

  const { data: pendingInvoice, isError } = useQuery({
    queryKey: ['pendingInvoice', enterpriseId],
    queryFn: async () => {
      const response = await getPendingInvoice(enterpriseId)
      return response.data
    },
    enabled: !!enterpriseId,
  })

  useEffect(() => {
    if (isError) {
      updateToast('Error loading pending invoice', 'error')
    }
  }, [isError, updateToast])

  const sendInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, poNumber }: { invoiceId: string; poNumber: string }) => {
      return sendEnterpriseInvoice(enterpriseId, { invoice_id: invoiceId, po_number: poNumber })
    },
    onSuccess: () => {
      updateToast('Invoice finalized and sent', 'success')
      setShowFinalizeDialog(false)
      setPoNumber('')
      queryClient.invalidateQueries({ queryKey: ['pendingInvoice', enterpriseId] })
    },
    onError: (err) => {
      if (isApiError(err)) {
        updateToast(err.detail ?? 'Error sending invoice', 'error')
      } else {
        updateToast('Error sending invoice', 'error')
      }
    },
  })

  const handleFinalizeInvoice = () => {
    if (!pendingInvoice?.pending_invoice_id || !poNumber.trim()) return
    sendInvoiceMutation.mutate({
      invoiceId: pendingInvoice.pending_invoice_id,
      poNumber: poNumber.trim(),
    })
  }

  if (!pendingInvoice?.pending_invoice_id) {
    return null
  }

  return (
    <>
      <div className={styles.pendingInvoiceSection}>
        <div className={styles.pendingInvoiceContent}>
          <span className={styles.pendingInvoiceTitle}>Pending Invoice</span>
          <span className={styles.pendingInvoiceText}>
            This enterprise has a draft invoice that needs to be finalized with a PO number.
          </span>
        </div>
        <Button title="Finalize Invoice" onClick={() => setShowFinalizeDialog(true)} type="dark" />
      </div>

      <DialogWrapper
        isOpen={showFinalizeDialog}
        title="Finalize Invoice"
        subtitle="Enter the PO number to finalize and send the invoice to the customer."
        close={() => setShowFinalizeDialog(false)}
      >
        <div className={sharedStyles.dialogContent}>
          <div className={sharedStyles.dialogDetailsSection}>
            <div className={styles.poNumberField}>
              <label className={styles.poNumberLabel}>PO Number</label>
              <Input type="text" value={poNumber} onInput={setPoNumber} placeholder="PO-2026-001" />
            </div>
          </div>
          <div className={sharedStyles.dialogActions}>
            <Button title="Cancel" onClick={() => setShowFinalizeDialog(false)} type="light" />
            <Button
              title="Finalize Invoice"
              onClick={handleFinalizeInvoice}
              type="dark"
              isLoading={sendInvoiceMutation.isPending}
              isDisabled={sendInvoiceMutation.isPending || !poNumber.trim()}
            />
          </div>
        </div>
      </DialogWrapper>
    </>
  )
}

export default PendingInvoiceSection
